import { useState, FormEvent } from "react";

const stages = ["Pre-Seed", "Seed", "Series A", "Series B+", "Bootstrapped"];
const helpTypes = ["融資", "策略合作", "Pitch Deck 優化", "生態資源對接", "其他"];

export default function SubmitProjectSection() {
  const [helpSelected, setHelpSelected] = useState<string[]>([]);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const toggleHelp = (val: string) => {
    setHelpSelected((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");

    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((val, key) => {
      if (key !== "help_type") data.append(key, val.toString());
    });
    data.append("help_type", helpSelected.join(", "));

    try {
      const res = await fetch("https://readdy.ai/api/form/d72jc09sakbatd8b3lig", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setHelpSelected([]);
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="submit" className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-amber-600 text-sm font-semibold tracking-widest uppercase mb-3">Apply Now</p>
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 leading-tight mb-4" style={{ fontFamily: "'Noto Serif TC', serif" }}>
            提交你的 AI 項目
          </h2>
          <p className="text-stone-500 text-base max-w-xl mx-auto leading-relaxed">
            如果你正在開發 AI 相關產品或服務，歡迎提交。我會親自審閱，並在適合的情況下幫你連接合適的資本。
          </p>
        </div>

        {/* Form Card */}
        {status === "success" ? (
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-12 text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-5">
              <i className="ri-check-line text-amber-600 text-2xl" />
            </div>
            <h3 className="text-stone-900 text-xl font-bold mb-2">提交成功！</h3>
            <p className="text-stone-500 text-sm">我已收到你的項目資訊，會在 7 天內親自回覆。感謝你的信任！</p>
            <button
              className="mt-6 text-amber-600 text-sm font-semibold hover:text-amber-700 cursor-pointer"
              onClick={() => setStatus("idle")}
            >
              提交另一個項目
            </button>
          </div>
        ) : (
          <form
            data-readdy-form
            id="submit-ai-project"
            onSubmit={handleSubmit}
            className="bg-stone-50 border border-stone-200 rounded-xl p-8 md:p-10 space-y-6"
          >
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">公司 / 項目名稱 <span className="text-amber-600">*</span></label>
                <input
                  name="company_name"
                  type="text"
                  required
                  placeholder="e.g. AIStar HK"
                  className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">創辦人姓名 <span className="text-amber-600">*</span></label>
                <input
                  name="founder_name"
                  type="text"
                  required
                  placeholder="你的全名"
                  className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 transition"
                />
              </div>
            </div>

            {/* LinkedIn */}
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1.5">LinkedIn 個人主頁</label>
              <input
                name="linkedin"
                type="url"
                placeholder="https://linkedin.com/in/yourname"
                className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 transition"
              />
            </div>

            {/* One-liner */}
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1.5">一句話描述（One-liner）<span className="text-amber-600">*</span></label>
              <input
                name="one_liner"
                type="text"
                required
                placeholder="e.g. 用 AI 幫中小企自動化客服，節省 80% 人力成本"
                className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 transition"
              />
            </div>

            {/* Problem & Solution */}
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1.5">問題與解決方案 <span className="text-amber-600">*</span></label>
              <textarea
                name="problem_solution"
                required
                rows={3}
                maxLength={500}
                onChange={(e) => setCharCount(e.target.value.length)}
                placeholder="你解決了什麼問題？你的方案是什麼？"
                className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 transition resize-none"
              />
              <p className={`text-xs mt-1 text-right ${charCount > 450 ? "text-red-400" : "text-stone-400"}`}>{charCount} / 500</p>
            </div>

            {/* Traction */}
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1.5">目前進度 / Traction <span className="text-amber-600">*</span></label>
              <input
                name="traction"
                type="text"
                required
                placeholder="e.g. 50 beta users, HK$200K ARR, MVP launched Q1 2026"
                className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 transition"
              />
            </div>

            {/* Stage + Amount */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">融資階段 <span className="text-amber-600">*</span></label>
                <select
                  name="stage"
                  required
                  defaultValue=""
                  className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 transition cursor-pointer"
                >
                  <option value="" disabled>請選擇</option>
                  {stages.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">目標融資金額</label>
                <input
                  name="target_amount"
                  type="text"
                  placeholder="e.g. USD 500K"
                  className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 transition"
                />
              </div>
            </div>

            {/* Help Type */}
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-2">希望獲得的幫助（可多選）</label>
              <div className="flex flex-wrap gap-2">
                {helpTypes.map((ht) => (
                  <button
                    key={ht}
                    type="button"
                    onClick={() => toggleHelp(ht)}
                    className={`text-xs font-medium px-3.5 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                      helpSelected.includes(ht)
                        ? "bg-amber-600 border-amber-600 text-white"
                        : "bg-white border-stone-200 text-stone-600 hover:border-amber-400"
                    }`}
                  >
                    {ht}
                  </button>
                ))}
              </div>
            </div>

            {/* Deck Link */}
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1.5">Pitch Deck 或 Demo 連結</label>
              <input
                name="pitch_deck"
                type="url"
                placeholder="Google Drive / Notion / YouTube 連結"
                className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 transition"
              />
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">聯絡 Email <span className="text-amber-600">*</span></label>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">WhatsApp 號碼</label>
                <input
                  name="whatsapp"
                  type="text"
                  placeholder="+852 9XXX XXXX"
                  className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 transition"
                />
              </div>
            </div>

            {/* Error */}
            {status === "error" && (
              <p className="text-red-500 text-sm text-center">提交失敗，請稍後再試。</p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={status === "submitting"}
              className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white font-bold py-4 rounded-md text-base transition-colors whitespace-nowrap cursor-pointer"
            >
              {status === "submitting" ? "提交中..." : "提交項目給我審核"}
            </button>

            <p className="text-stone-400 text-xs text-center">
              提交即代表你同意我在保密協議下審閱你的項目資料。
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
